// The trained mouth SUBSPACE, and the box the training data occupies inside it.
//
// WHAT THIS IS FOR. ARKit writes the mouth controls independently. Each one can be
// inside its own trained range while the COMBINATION is something the capture never
// contained — and because `geom` and `appr` are both conditioned on the trunk's one
// `code` latent, an unseen combination degrades them together: offsets overshoot
// and colour runs to white at the same time. Per-control range limiting cannot see
// it (every axis is in range) and neither can a distance threshold (measured on
// eyeline-v10c, ordinary speech sits as far off the trained manifold as the slider
// poses that blow up).
//
// WHY NOT THE MOUTH CONTROLLER. That one replaces the whole mouth block with a
// closed<->open interpolation, which is on-manifold by construction but is ONE
// dimensional: 30 of ARKit's 52 channels go inert, every viseme with them. It is
// right for a deliberate slider pose and wrong as a default for a talking face.
//
// WHAT THIS DOES INSTEAD. The 94 distinct trained poses span a subspace of the
// 108-control mouth block. Decompose the requested mouth block in that basis, clamp
// each coordinate to the range the training data actually covers, and drop the part
// that lies outside the span altogether. Expressions the capture contains survive —
// they are IN the span, at in-range coordinates — while a combination the capture
// never contained is pulled back to the nearest point of the trained box.
//
// It is the same idea as the per-control trained limits (src/lib/rigBoard.js), moved
// from the control axes to the axes the data is actually shaped along, which is the
// only place a COMBINATION is expressible as a coordinate.
//
// HONEST LIMITS, measured on eyeline-v10c and not yet improved on:
//   - it keeps 80-94% of each single channel's effect, so it is not a mute button;
//   - but it pulls a 5-channel mouth combination only from 1.38 to 1.07 away from
//     the nearest trained pose, against 0.46-0.71 for single channels. With 79
//     components over 94 samples the box is permissive. Keeping fewer components
//     constrains harder and costs more expression — that trade is the `components`
//     argument, and there is no setting yet PROVEN to keep visemes and kill the
//     over-response at once.
//
// Pure: no DOM, no WebGPU, so `node --test` reaches all of it.

/** Rows of `presets` that are not exact duplicates of an earlier row.
 *
 *  The pose table repeats itself — the canonicalisation gives every frame of a
 *  cluster its medoid's rig, so eyeline-v10c's 246 rows hold 94 distinct vectors.
 *  Duplicates would weight the PCA toward whichever configurations were filmed
 *  most and contribute nothing to the span or the box. */
export function distinctRowIndices(presets, rows, cols) {
  const seen = new Set();
  const out = [];
  for (let r = 0; r < rows; r++) {
    let key = "";
    for (let i = 0; i < cols; i++) key += `${presets[r * cols + i].toFixed(6)},`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

/**
 * Eigendecomposition of a symmetric matrix by cyclic Jacobi rotations.
 *
 * Jacobi rather than power iteration with deflation: the deflating version needs
 * hundreds of iterations PER component and there are ~79 of them here, which is
 * seconds of boot. Jacobi gets the whole spectrum in a handful of sweeps, and for a
 * symmetric matrix it is unconditionally stable — no start vector to get unlucky
 * with (a uniform start on a centred Gram matrix lands exactly on the null
 * direction and returns nothing at all, which is a real way to get this wrong).
 *
 * @param {Float64Array} A row-major n x n, symmetric. NOT modified.
 * @returns {{values: Float64Array, vectors: Float64Array}} vectors row-major,
 *          column k of `vectors` is the eigenvector for `values[k]`.
 */
export function jacobiEigenSymmetric(A, n, { sweeps = 60, tol = 1e-12 } = {}) {
  const a = Float64Array.from(A);
  const v = new Float64Array(n * n);
  for (let i = 0; i < n; i++) v[i * n + i] = 1;
  for (let sweep = 0; sweep < sweeps; sweep++) {
    let off = 0;
    for (let p = 0; p < n; p++) for (let q = p + 1; q < n; q++) off += a[p * n + q] * a[p * n + q];
    if (off <= tol) break;
    for (let p = 0; p < n - 1; p++) {
      for (let q = p + 1; q < n; q++) {
        const apq = a[p * n + q];
        if (Math.abs(apq) < 1e-300) continue;
        const app = a[p * n + p], aqq = a[q * n + q];
        const theta = (aqq - app) / (2 * apq);
        const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
        const c = 1 / Math.sqrt(t * t + 1);
        const s = t * c;
        for (let k = 0; k < n; k++) {
          const akp = a[k * n + p], akq = a[k * n + q];
          a[k * n + p] = c * akp - s * akq;
          a[k * n + q] = s * akp + c * akq;
        }
        for (let k = 0; k < n; k++) {
          const apk = a[p * n + k], aqk = a[q * n + k];
          a[p * n + k] = c * apk - s * aqk;
          a[q * n + k] = s * apk + c * aqk;
        }
        for (let k = 0; k < n; k++) {
          const vkp = v[k * n + p], vkq = v[k * n + q];
          v[k * n + p] = c * vkp - s * vkq;
          v[k * n + q] = s * vkp + c * vkq;
        }
      }
    }
  }
  const values = new Float64Array(n);
  for (let i = 0; i < n; i++) values[i] = a[i * n + i];
  return { values, vectors: v };
}

/**
 * Build the trained mouth subspace + its coordinate box.
 *
 * @param {object} o
 * @param {Float32Array} o.presets flat pose table
 * @param {number} o.rows, @param {number} o.cols table shape
 * @param {number[]} o.mouthIdx the control indices forming the block
 * @param {number|null} [o.components] keep only the top-k (null = every component
 *        the data spans). FEWER constrains harder and costs more expression.
 * @param {number} [o.pad] widen each coordinate range by this fraction of its own
 *        span, so the box is not a hard edge exactly at the extreme training
 *        sample. Mirrors `trainedLimits`' pad, which exists for the same reason.
 * @returns {null|{k:number, dims:number, energy:number, projectInto:Function, coordsOf:Function}}
 */
export function buildMouthSubspace({ presets, rows, cols, mouthIdx, components = null, pad = 0.05, extraPoses = [] }) {
  if (!presets || !rows || !cols || !mouthIdx?.length) return null;
  const idx = mouthIdx.filter((i) => Number.isInteger(i) && i >= 0 && i < cols);
  const D = idx.length;
  if (!D) return null;
  const use = distinctRowIndices(presets, rows, cols);
  // EXTRA POSES are samples that are not rows of the table — in practice the one
  // pose the avatar idles at. That pose can be a COMBINATION (row 0's face with a
  // closed trained mouth grafted on), which is by construction not a trained
  // full-rig vector, so without it the fit does not span the idle face and the
  // projection displaces it: measured 0.0896 on eyeline-v10c over the whole rig,
  // against 0.0000 once it is included. Idle is the pose the avatar spends most of
  // its time in, so "reproduced exactly" is not negotiable for it.
  const extras = (extraPoses ?? []).filter((p) => p && p.length >= cols);
  const N = use.length + extras.length;
  if (N < 3) return null;

  const mean = new Float64Array(D);
  const X = new Float64Array(N * D);
  for (let r = 0; r < use.length; r++) {
    const base = use[r] * cols;
    for (let i = 0; i < D; i++) {
      const val = presets[base + idx[i]];
      X[r * D + i] = val;
      mean[i] += val / N;
    }
  }
  for (let e = 0; e < extras.length; e++) {
    const r = use.length + e;
    for (let i = 0; i < D; i++) {
      const val = extras[e][idx[i]];
      X[r * D + i] = val;
      mean[i] += val / N;
    }
  }
  for (let r = 0; r < N; r++) for (let i = 0; i < D; i++) X[r * D + i] -= mean[i];

  // Gram (N x N) rather than covariance (D x D): N < D here, and the eigenvectors
  // of the Gram matrix carry straight over to feature directions.
  const G = new Float64Array(N * N);
  for (let p = 0; p < N; p++) {
    for (let q = p; q < N; q++) {
      let s = 0;
      for (let i = 0; i < D; i++) s += X[p * D + i] * X[q * D + i];
      G[p * N + q] = s; G[q * N + p] = s;
    }
  }
  const { values, vectors } = jacobiEigenSymmetric(G, N);
  const order = Array.from({ length: N }, (_, i) => i).sort((a, b) => values[b] - values[a]);

  const total = order.reduce((a, i) => a + Math.max(0, values[i]), 0);
  const wanted = Number.isInteger(components) && components > 0 ? components : N;
  const basis = [];
  let energy = 0;
  for (const e of order) {
    if (basis.length >= wanted) break;
    const lam = values[e];
    // A near-zero eigenvalue is a direction the data does not span at all; its
    // "eigenvector" is numerical noise and projecting onto it would admit an
    // arbitrary coordinate, which is the opposite of the point.
    if (!(lam > 1e-9 * (total || 1))) continue;
    const u = new Float64Array(D);
    for (let r = 0; r < N; r++) {
      const w = vectors[r * N + e];
      if (!w) continue;
      for (let i = 0; i < D; i++) u[i] += w * X[r * D + i];
    }
    let n = 0;
    for (let i = 0; i < D; i++) n += u[i] * u[i];
    n = Math.sqrt(n);
    if (n < 1e-9) continue;
    for (let i = 0; i < D; i++) u[i] /= n;
    basis.push(u);
    energy += Math.max(0, lam);
  }
  const K = basis.length;
  if (!K) return null;

  // The box: each coordinate's range over the trained poses.
  const lo = new Float64Array(K).fill(Infinity);
  const hi = new Float64Array(K).fill(-Infinity);
  for (let r = 0; r < N; r++) {
    for (let k = 0; k < K; k++) {
      let c = 0;
      const u = basis[k];
      for (let i = 0; i < D; i++) c += X[r * D + i] * u[i];
      if (c < lo[k]) lo[k] = c;
      if (c > hi[k]) hi[k] = c;
    }
  }
  for (let k = 0; k < K; k++) {
    const m = pad * (hi[k] - lo[k]);
    lo[k] -= m; hi[k] += m;
  }

  // Preallocated: this runs on the per-frame path.
  const coefs = new Float64Array(K);
  const centred = new Float64Array(D);

  /** Coordinates of a rig's mouth block, before clamping — diagnostics/tests. */
  const coordsOf = (rig) => {
    const out = new Float64Array(K);
    for (let i = 0; i < D; i++) centred[i] = rig[idx[i]] - mean[i];
    for (let k = 0; k < K; k++) {
      let c = 0;
      const u = basis[k];
      for (let i = 0; i < D; i++) c += centred[i] * u[i];
      out[k] = c;
    }
    return out;
  };

  /**
   * Clamp `rig`'s mouth block into the trained box, IN PLACE.
   *
   * Two things happen and both matter: coordinates outside the box are clamped
   * (the combination is pulled back to the nearest trained configuration along
   * those axes), and whatever lay outside the span is dropped by reconstruction
   * (it is, by definition, a direction no trained pose ever moved along).
   *
   * @returns {number} how far the block moved, so a caller can report it.
   */
  const projectInto = (rig) => {
    for (let i = 0; i < D; i++) centred[i] = rig[idx[i]] - mean[i];
    for (let k = 0; k < K; k++) {
      let c = 0;
      const u = basis[k];
      for (let i = 0; i < D; i++) c += centred[i] * u[i];
      coefs[k] = c < lo[k] ? lo[k] : c > hi[k] ? hi[k] : c;
    }
    let moved = 0;
    for (let i = 0; i < D; i++) {
      let v = mean[i];
      for (let k = 0; k < K; k++) v += coefs[k] * basis[k][i];
      const d = v - rig[idx[i]];
      moved += d * d;
      rig[idx[i]] = v;
    }
    return Math.sqrt(moved);
  };

  return { k: K, dims: D, poses: N, extras: extras.length, energy: total > 0 ? energy / total : 1, projectInto, coordsOf };
}


/**
 * Anatomical region of a control, from its name.
 *
 * The unit the projection is fitted over. A control should only be constrained by
 * others that move the same part of the face — a global fit over all 168 couples
 * regions the data says are independent: measured on eyeline-v10c the two mouth
 * corners correlate at -0.075 (they move independently) yet a global projection
 * bled 0.081 of a left frown onto the right corner.
 *
 * SIDES ARE SPLIT EVERYWHERE EXCEPT THE MOUTH, and the asymmetry is deliberate.
 * The left and right eye are different parts of a face; driving one has no business
 * moving the other, whether or not the actor happened to wink (he almost never did:
 * L/R blink correlate at 0.930). But the combinations that make the decoders
 * over-respond DO span both sides of the mouth — the reported five-channel case
 * does — and splitting the mouth takes the constraint on those from 1.25 to 1.51
 * against 1.56 unconstrained, i.e. gives up nearly all of it.
 *
 * Measured (wink: driven/other-eye, frown: driven/other-corner, combo distance):
 *
 *   none              0.667 / 0.000    0.843 / 0.000    1.56
 *   global            0.280 / 0.060    0.551 / 0.081    1.17
 *   sides joined      0.325 / 0.026    0.689 / 0.048    1.25
 *   sides split       0.348 / 0.000    0.759 / 0.000    1.51
 *   THIS (not mouth)  0.348 / 0.000    0.689 / 0.048    1.25
 *
 * which is the joined row on the mouth and the split row on the eyes, dominating
 * both. The residual 0.048 bleed between the two mouth corners is the price of
 * still catching the combinations, and is the one place the regions stay coarse.
 */
export function controlRegion(name) {
  if (/brow/i.test(name)) return "brow";
  if (/eye|eyelash/i.test(name)) return "eye";
  if (/nose/i.test(name)) return "nose";
  if (/cheek/i.test(name)) return "cheek";
  if (/mouth|jaw|lip|chin|teeth/i.test(name)) return "mouth";
  return "other";
}

/**
 * The grouping the projection uses: anatomical part, ALWAYS split by side.
 *
 * A control should only be constrained by others that move the same part of the
 * same side of the face. The mouth was joined across sides for a while, on the
 * theory that the combinations which make the decoders over-respond span both —
 * but that was a proxy measurement, and side-splitting the mouth reads better in
 * practice. It is also the same argument already accepted for the eyes: a right
 * frown is no more a function of a left frown than a right blink is of a left one.
 *
 * The split is structural, not a tuning: buildRegionalSubspaces fits one subspace
 * per key over that key's columns only, so a left-side request CANNOT move a
 * right-side control. `?rigpca=global` is the single-fit A/B, which does couple
 * them (measured: 60 right-side controls move on a left wink; here, 0).
 */
export function regionKey(name) {
  const part = controlRegion(name);
  const side = /^CTRL_([LRC])_/.exec(name)?.[1] ?? "C";
  return `${side}_${part}`;
}

/**
 * One subspace per region, applied in turn.
 *
 * @returns {null|{regions: Array<{region:string,dims:number,k:number}>,
 *                 projectInto:(rig:Float32Array)=>number}}
 */
export function buildRegionalSubspaces({ presets, rows, cols, names, extraPoses = [], groupBy = regionKey }) {
  if (!names?.length || !presets) return null;
  const byKey = new Map();
  names.forEach((n, i) => {
    if (i >= cols) return;
    const k = groupBy(n);
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(i);
  });
  const built = [];
  for (const [region, idx] of byKey) {
    // A one-control region has no combination to constrain; skip rather than fit a
    // degenerate basis over it.
    if (idx.length < 2) continue;
    const sub = buildMouthSubspace({ presets, rows, cols, mouthIdx: idx, extraPoses });
    if (sub) built.push({ region, sub });
  }
  if (!built.length) return null;
  return {
    regions: built.map(({ region, sub }) => ({ region, dims: sub.dims, k: sub.k })),
    projectInto(rig) {
      let moved = 0;
      for (const { sub } of built) moved += sub.projectInto(rig);
      return moved;
    },
  };
}

/**
 * `?rigpca=` — the ONE switch for the trained-subspace projection.
 *
 *   (absent)  per region, sides split EXCEPT the mouth   <- default
 *   part      per region, sides joined
 *   side      per region and per side, mouth included
 *   global    one fit over all 168 controls
 *   off / 0   no projection at all
 *
 * One flag rather than two. This started life as a mouth-only constraint and grew
 * to cover the whole rig, which left an on/off switch named `mouthpca` gating a
 * whole-RIG projection plus a second `pcaall` for the scope — two knobs on one
 * thing, one of them misnamed. Scope and off are the same decision, so they are the
 * same flag.
 *
 * `components` survives as a build option (fewer = a harder constraint, less
 * expression) but is deliberately not URL-selectable: below the full basis the fit
 * stops spanning the idle pose and moves the neutral face, which is not something
 * to hand someone a knob for.
 */
/**
 * The no-flag scope. `global` is the single fit over the whole rig; `region` is
 * the per-part, per-side fit (regionKey), kept as the A/B. Flipping the default
 * is this one constant.
 *
 * The two differ in ONE measured way that matters: the global fit couples
 * regions, so a left-side request moves right-side controls (60 of them on a
 * left wink; per-region: 0). It constrains harder in exchange, because the
 * combinations that make the decoders over-respond do span sides, and it is
 * CONTAINED in the regional fit — splitting into independently-projected blocks
 * is a relaxation, not a different constraint.
 */
export const DEFAULT_SCOPE = "global";

/**
 * The least of an expression the projection may keep before it is doing more harm
 * than good — the median over the 52 single-ARKit-channel poses, measured against
 * the base the live path composes onto.
 *
 * The fit only removes what the capture never contained, so on a bundle with a
 * rich pose table it is nearly free: eyeline-v10c keeps 80-94% of every single
 * channel (and still moves 74-78 controls). But the basis can span no more
 * directions than the table has DISTINCT poses, so a small table constrains
 * everything, including ordinary speech: myragaussainsplatting's 30-row table
 * gives 29 components over 168 controls and keeps ~25% — measured over a real
 * reply, mouth-control travel is 0.10 with the projection and 0.40 without, and
 * the render is frame-for-frame identical while the character talks. A face that
 * cannot move is not a safer face than one that occasionally over-responds, and
 * unlike the over-response it does not announce itself.
 *
 * Half is the line because it is far from both measured populations (0.80+ vs
 * 0.25) rather than tuned to either.
 */
export const MIN_EXPRESSION_RETENTION = 0.5;

/**
 * How much of each pose the projection KEEPS, relative to `base`.
 *
 * Both sides are measured AFTER projection — the base is projected too — so this
 * is the fit's effect on the EXPRESSION, not on where the face sits. A base that
 * is itself in the span (it is: it rides in as `extraPoses`) makes that a no-op,
 * and a base that is not would otherwise show up as lost expression it did not
 * cost.
 *
 * @param {{projectInto: (rig: Float32Array) => number}} subspace
 * @param {Float32Array} base   the pose the live path composes onto
 * @param {Float32Array[]} poses  base + one expression each
 * @returns {{median: number, min: number, kept: number[]}|null}
 */
export function subspaceRetention(subspace, base, poses) {
  if (!subspace?.projectInto || !base?.length || !poses?.length) return null;
  const projBase = Float32Array.from(base);
  subspace.projectInto(projBase);
  const kept = [];
  for (const pose of poses) {
    if (pose?.length !== base.length) continue;
    let raw = 0;
    for (let i = 0; i < base.length; i++) { const d = pose[i] - base[i]; raw += d * d; }
    raw = Math.sqrt(raw);
    // A channel this control space cannot express at all says nothing about the
    // projection — it was already zero before the fit saw it.
    if (raw < 1e-4) continue;
    const proj = Float32Array.from(pose);
    subspace.projectInto(proj);
    let out = 0;
    for (let i = 0; i < base.length; i++) { const d = proj[i] - projBase[i]; out += d * d; }
    kept.push(Math.sqrt(out) / raw);
  }
  if (!kept.length) return null;
  const sorted = [...kept].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  return { median, min: sorted[0], kept };
}

/**
 * Which scope's fit is actually installed, once the retention gate has had its say.
 *
 * The gate used to be a two-way choice — the DEFAULT (global) fit, or no projection at
 * all — and on a thin pose table it always took the second. That is the worst of the
 * three outcomes: the projection is the only stage that removes a combination which is
 * in range on every axis and yet never appeared in the capture, so dropping it leaves
 * the decoders extrapolating on exactly the bundles least able to afford it. Measured on
 * myragaussainsplatting (30 rows, global retention 0.325, so the gate dropped it): the
 * face carried a median of 55 oversized head splats while nothing but its own idle ARKit
 * animation played, rising with how far the rig travelled — 36 in the quietest third of
 * frames against 73 in the busiest.
 *
 * The regional fit is the missing middle. It is the SAME constraint relaxed — one block
 * per region instead of one fit over all 168 controls — so it can only keep more
 * expression than the global one, and on that bundle it keeps enough to pass while still
 * removing every oversized splat (0 median, and rig travel 0.589 against 0.658 with no
 * projection at all, where the global fit left 0.223).
 *
 * So: try the default, fall back to the looser scope, and only then give up. An explicit
 * ?rigpca= is a decision and is never second-guessed, in either direction.
 *
 * @param {object} o
 *   scope       the scope resolveRigProjection settled on
 *   explicit    whether a URL asked for it
 *   retentionOf (scope) => median retention, or null when there is nothing to measure
 *   min         the bar, default MIN_EXPRESSION_RETENTION
 * @returns {{scope: string, retention: number|null, fellBack: boolean, dropped: boolean,
 *            tried: Record<string, number|null>}}
 */
export function chooseProjectionScope({ scope, explicit, retentionOf, min = MIN_EXPRESSION_RETENTION }) {
  const tried = {};
  const measure = (s) => { const r = retentionOf(s); tried[s] = r; return r; };
  const first = measure(scope);
  // Nothing measurable (no pose table, no fit): the caller's own "none" case.
  if (first == null) return { scope, retention: null, fellBack: false, dropped: false, tried };
  if (explicit || first >= min) return { scope, retention: first, fellBack: false, dropped: false, tried };
  if (scope !== "region") {
    const alt = measure("region");
    if (alt != null && alt >= min) {
      return { scope: "region", retention: alt, fellBack: true, dropped: false, tried };
    }
  }
  return { scope, retention: first, fellBack: false, dropped: true, tried };
}

export function resolveRigProjection(search) {
  const qp = typeof search === "string" ? new URLSearchParams(search) : search;
  const raw = (qp?.get("rigpca") ?? "").toLowerCase();
  if (raw === "off" || raw === "0") return { enabled: false, scope: DEFAULT_SCOPE, components: null, explicit: true };
  if (raw === "global") return { enabled: true, scope: "global", components: null, explicit: true };
  // `side` and `part` are retired names for regional scopes. They resolve to
  // `region` rather than to the default, because the default is now the GLOBAL
  // fit and that is the opposite of what either asked for: both were requests for
  // MORE separation between regions, and silently answering them with the one
  // scope that couples regions would invert a stale URL's meaning.
  if (raw === "region" || raw === "side" || raw === "part") {
    return { enabled: true, scope: "region", components: null, explicit: true };
  }
  // Anything unrecognised falls through to the default rather than disabling the
  // projection: a typo must never silently ship an unconstrained rig.
  // `explicit` false: nobody asked for this scope, so the retention gate in
  // OGSModel may still drop the fit on a bundle whose table is too small to let
  // the character speak. A typed flag is a decision and is never overridden.
  return { enabled: true, scope: DEFAULT_SCOPE, components: null, explicit: false };
}
